import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { debounce } from 'lodash';

import { useModalManager } from '../../../../hooks/ModalManager.hook';
import { useAppDispatch } from '../../../../hooks/useAppDispatcher.hook';

import {
  createVacancyControlThunk,
  deleteVacancyControlThunk,
  getVacancyControlListThunk,
  updateVacancyControlThunk,
} from '../../../../redux/ControlPanel/VacancyControl/VacancyControl.thunk';

import CardComponent from '../../../../components/Card.component';
import ButtonComponent from '../../../../components/Button.component';
import InputComponent from '../../../../components/Input/Input.component';
import SpinnerComponent from '../../../../components/Spinner.component';
import ControlCreateEditModal from '../../../../components/Modal/Control/ControlCreateEditModal';
import ConfirmDuplicateModal from '../../../../components/Modal/Control/ConfirmDuplicateModal';
import ControlSectionContent from './ControlSectionContent.component';

import { SortTypes } from '../../../../types/sort.types';

import SearchIcon from '../../../../components/Icons/Search.icon';
import SortArrowsIcon from '../../../../components/Icons/SortArrows.icon';

import {
  StyledControlListItem,
  StyledControlSectionContainer,
  StyledControlSectionHeader,
  StyledHeaderButtonsWrapper,
  StyledHeaderLabel,
  StyledHeaderTitle,
  StyledSearchIcon,
  StyledSearchWrapper,
  StyledSortContainer,
} from './sharedStyledComponents';
import { checkUserRequiredPermissions } from '../../../../utils/getUserRolesByToken';
import { UserPermissions } from '../../../../types/userPermissions.types';
import useAuthHook from '../../../../hooks/useAuth.hook';
import { ControlSectionTypes } from '../../../../redux/ControlPanel/ControlPanelList/ControlPanelList.type';
import { useSelector } from 'react-redux';
import {
  getVacancyControlList,
  getVacancyControlListPagination,
  setVacancyControlList,
} from '../../../../redux/ControlPanel/VacancyControl/VacancyControl.slice';
import { Vacancy } from '../../../../redux/ControlPanel/VacancyControl/VacancyControl.type';

const sectionType = ControlSectionTypes.VACANCIES;

export const VacanciesSectionComponent: FC = () => {
  const { user } = useAuthHook();
  const { openModal } = useModalManager();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setLoading] = useState(false);
  const [orderSort, setOrderSort] = useState(SortTypes.ASC);

  const vacancies = useSelector(getVacancyControlList);
  const pagination = useSelector(getVacancyControlListPagination);

  const editable = checkUserRequiredPermissions(user.permissions, [
    UserPermissions.CAN_EDIT_JOB,
  ]);

  const creatable = checkUserRequiredPermissions(user.permissions, [
    UserPermissions.CAN_CREATE_JOB,
  ]);

  const deletable = checkUserRequiredPermissions(user.permissions, [
    UserPermissions.CAN_DELETE_JOB,
  ]);

  const viewable = checkUserRequiredPermissions(user.permissions, [
    UserPermissions.CAN_VIEW_JOB,
  ]);

  const fetchParams = useMemo(() => {
    return {
      q: searchQuery,
      sort: `${orderSort === SortTypes.DESC ? '-' : ''}name`,
    };
  }, [orderSort, searchQuery]);

  const createItemWithModalWrapper = async (
    fetchFunc: Function,
    updateFunc: Function,
    payload: any,
    updateFetchParams?: any,
    message?: string,
  ) => {
    dispatch(fetchFunc(payload)).then((res: any) => {
      if (!res.payload && !!res.error) {
        openModal(ConfirmDuplicateModal, {
          message: message ?? t(`Such a record already exists!`),
        });
      } else {
        dispatch(updateFunc(updateFetchParams));
      }
    });
  };

  const updateListAfterAction = () => {
    dispatch(getVacancyControlListThunk(fetchParams));
  };

  const createNewSectionItem = (newItem: Vacancy) => {
    createItemWithModalWrapper(
      createVacancyControlThunk,
      getVacancyControlListThunk,
      {
        name: newItem.name,
      },
      fetchParams,
    );
  };

  const editSectionItem = (sectionItem: Vacancy) => {
    createItemWithModalWrapper(
      updateVacancyControlThunk,
      getVacancyControlListThunk,
      sectionItem,
      fetchParams,
    );
  };

  const deleteSectionItem = (sectionItem: Vacancy) => {
    dispatch(deleteVacancyControlThunk(sectionItem.id)).then(() =>
      updateListAfterAction(),
    );
  };

  const handleSearch = (value: string) => {
    setLoading(true);

    dispatch(
      getVacancyControlListThunk({
        q: value,
        page: 1,
        sort: `${orderSort === SortTypes.DESC ? '-' : ''}name`,
      }),
    ).finally(() => setLoading(false));
  };

  const debounceSearchValue = useCallback(
    debounce(handleSearch, 300) as Function,
    [],
  );

  const handleSortChange = (sortOrder: SortTypes) => {
    handleSort(sortOrder);

    dispatch(
      getVacancyControlListThunk({
        page: 1,
        q: searchQuery,
        sort: `${sortOrder === SortTypes.ASC ? '-' : ''}name`,
      }),
    );
  };

  const handleSort = (sortOrder: SortTypes) => {
    setOrderSort(sortOrder === SortTypes.ASC ? SortTypes.DESC : SortTypes.ASC);
  };

  const updateItems = (items: Vacancy[]) => {
    dispatch(setVacancyControlList(items));
  };

  useEffect(() => {
    dispatch(getVacancyControlListThunk(fetchParams));
  }, []);

  if (!viewable) return null;

  return (
    <CardComponent>
      <StyledControlSectionContainer>
        <StyledControlSectionHeader>
          <StyledHeaderTitle>
            <StyledHeaderLabel>{t('Vacancy management')}</StyledHeaderLabel>

            <StyledSortContainer onClick={() => handleSortChange(orderSort)}>
              <SortArrowsIcon sortOrder={orderSort} svgId={sectionType} />
            </StyledSortContainer>
          </StyledHeaderTitle>

          <StyledHeaderButtonsWrapper>
            {creatable && (
              <ButtonComponent
                handleClick={() =>
                  openModal(ControlCreateEditModal, {
                    sectionType,
                    createNewSectionItem,
                    editable,
                    deletable,
                  })
                }
                textColor="#588fff"
                customColor="#ebf0ff"
              >
                {t('Create')}
              </ButtonComponent>
            )}
          </StyledHeaderButtonsWrapper>
        </StyledControlSectionHeader>

        <StyledSearchWrapper>
          <InputComponent
            value={searchQuery}
            onChange={(searchValue: string) => {
              setSearchQuery(searchValue);
              debounceSearchValue(searchValue);
            }}
            onEnter={handleSearch}
            placeholder={t(`Search ${sectionType}`)}
            right={
              <StyledSearchIcon>
                {isLoading ? <SpinnerComponent size="small" /> : <SearchIcon />}
              </StyledSearchIcon>
            }
          />
        </StyledSearchWrapper>
        <ControlSectionContent
          isLoadMode={!(pagination?.currentPage === pagination?.lastPage)}
          initialData={vacancies}
          fetchData={getVacancyControlListThunk}
          sectionType={sectionType}
          fetchParams={{
            q: searchQuery,
            sort: `${orderSort === SortTypes.DESC ? '-' : ''}name`,
            page: pagination?.currentPage || 1,
          }}
          updateItems={updateItems}
          renderItem={item => (
            <StyledControlListItem
              key={item.id}
              highlighted={item.isAllRegions}
              onClick={() =>
                openModal(ControlCreateEditModal, {
                  sectionType,
                  sectionItem: item,
                  editSectionItem,
                  deleteSectionItem,
                  editable,
                  deletable,
                })
              }
            >
              {item.truncatedDisplayName || item.name}
            </StyledControlListItem>
          )}
        />
      </StyledControlSectionContainer>
    </CardComponent>
  );
};
